import { ASSETS } from "@/data/content";
import PageHero from "@/components/arc/PageHero";
import Reveal from "@/components/arc/Reveal";
import Marquee from "@/components/arc/Marquee";
import { SectionHeader, GlowButton, HudRow, StatusBadge, Panel } from "@/components/arc/ui";
import { GlowOrbs } from "@/components/arc/Backdrops";

const PILLARS = [
  {
    title: "RISE",
    body: "Machines gain identity. A persistent self that can be addressed, verified, and remembered — the first step from tool to participant.",
  },
  {
    title: "PROTECT",
    body: "Machines earn trust. Reputation built from verified missions protects the network from anonymous failure and gives autonomy a foundation.",
  },
  {
    title: "TRANSACT",
    body: "Machines join the economy. Wallets, payments in USDC, and machine-to-machine exchange inside the Arc ecosystem.",
  },
];

const About = () => (
  <>
    <PageHero
      index="00"
      eyebrow="COMMAND"
      title="WHAT IS ARCIMUS"
      sub="An original humanoid machine commander — a brand, a lore universe, and a protocol design study for the coming machine economy."
    />

    <section className="relative overflow-hidden py-20 md:py-32">
      <GlowOrbs />
      <div className="relative mx-auto grid max-w-[1600px] items-center gap-14 px-5 md:px-8 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <Reveal>
            <SectionHeader index="01" eyebrow="THESIS" title="A LEGENDARY MACHINE COMMANDER" />
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 space-y-5 text-base leading-relaxed text-silver md:text-lg">
              <p>
                ARCIMUS represents a future where humanoid machines are no longer passive software
                or simple robots. They carry identity, wallets, missions, reputation, and
                autonomous economic agency across decentralized infrastructure.
              </p>
              <p>
                The project is built around the ARC ecosystem and its USDC-native economic
                environment — a setting where machine payments and on-chain machine identity make
                narrative and technical sense.
              </p>
              <p className="text-chrome">
                Everything you see here is honest about what it is: a live brand and concept with
                simulated interfaces, structured so that real Web3 capability can plug in as it
                becomes real.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-10 flex flex-wrap gap-4">
              <GlowButton to="/lore" testId="about-lore-btn">READ THE LORE</GlowButton>
              <GlowButton to="/docs" variant="ghost" testId="about-docs-btn">READ THE DOCS</GlowButton>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <Panel className="p-8" data-testid="about-identity-panel">
            <div className="flex items-center justify-between">
              <img src={ASSETS.logo} alt="ARCIMUS emblem" className="h-20 w-20 rounded-full object-cover ring-1 ring-ice/30" />
              <StatusBadge status="LIVE" />
            </div>
            <div className="mt-8">
              <HudRow label="UNIT" value="ARCIMUS-001" accent />
              <HudRow label="CLASS" value="PRIME" />
              <HudRow label="SYSTEM STATUS" value="LIVE" accent />
              <HudRow label="NETWORK" value="ARC" />
              <HudRow label="PROJECT STATUS" value="ACTIVE" />
              <HudRow label="ECONOMY" value="USDC — CONCEPTUAL" />
            </div>
            <p className="mt-6 font-term text-[10px] leading-relaxed tracking-[0.2em] text-silver/70">
              STATUS VALUES DESCRIBE THE BRAND AND THIS WEBSITE. NO LIVE AUTONOMOUS MACHINE IS
              DEPLOYED.
            </p>
          </Panel>
        </Reveal>
      </div>
    </section>

    <section className="relative border-t border-ice/10 bg-space/50 py-20 md:py-32">
      <div className="mx-auto max-w-[1600px] px-5 md:px-8">
        <Reveal>
          <SectionHeader index="02" eyebrow="DIRECTIVES" title="RISE. PROTECT. TRANSACT." align="center" />
        </Reveal>
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <Panel className="h-full p-8 transition-shadow duration-300 hover:shadow-[0_0_40px_rgba(22,119,255,0.25)]">
                <span className="font-term text-[10px] tracking-[0.3em] text-electric">
                  DIRECTIVE {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-4 font-display text-2xl font-bold tracking-[0.2em] text-chrome">{p.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-silver">{p.body}</p>
              </Panel>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="scanlines relative overflow-hidden py-20 md:py-28">
      <div className="absolute inset-0" aria-hidden="true">
        <img src={ASSETS.background} alt="" className="h-full w-full object-cover object-[center_60%] opacity-30" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-abyss via-space/80 to-abyss" />
      </div>
      <div className="relative mx-auto max-w-4xl px-5 text-center md:px-8">
        <Reveal>
          <p className="font-term text-[11px] tracking-[0.4em] text-electric">03 {"//"} TRANSMISSION</p>
          <blockquote className="mt-8 font-display text-2xl font-bold leading-snug tracking-[0.06em] text-chrome text-glow-blue sm:text-3xl lg:text-4xl">
            “A HUMANOID COMMANDER HAS ENTERED AN ON-CHAIN WORLD.”
          </blockquote>
          <p className="mt-8 font-term text-xs tracking-[0.3em] text-silver">
            ARCIMUS {"//"} THE FIRST PRIME OF ARC
          </p>
        </Reveal>
      </div>
    </section>

    <Marquee />
  </>
);

export default About;
